import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPedidoComercial,
  getAllClients,
  getAllPaymentMethods,
  getPedidoComercial,
  updatePedidoComercial,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { getAuthPermissoes } from '../services/authStorage';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';
import PageHeader from './common/PageHeader';
import {
  processarBuscaProdutoEnter,
  PRODUCT_SEARCH_HINT,
} from '../utils/productSearch';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const precoVarejo = (produto) => {
  const precos = produto?.precos || produto?.Precos || [];
  const row = precos.find((p) => p.tipo === 0 || p.tipo === 'Varejo' || p.Tipo === 0);
  return Number(row?.valor ?? row?.Valor ?? produto?.valor ?? 0) || 0;
};

/** @param {{ isEditMode?: boolean, tipoFixo?: 'Orcamento' | 'Pedido' }} props */
export default function FormPedidoComercial({ isEditMode = false, tipoFixo }) {
  const { id } = useParams();
  const [search] = useSearchParams();
  const editing = isEditMode || Boolean(id);
  const navigate = useNavigate();
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes() || {}, []);
  const canAlterarPreco = Boolean(
    permissions.alteraPrecoOrcamento || permissions.alteraPreco,
  );

  const tipoInicial = tipoFixo
    || (search.get('tipo') === 'Pedido' ? 'Pedido' : 'Orcamento');
  const [tipo, setTipo] = useState(tipoInicial);
  const basePath = (tipoFixo || tipo) === 'Pedido' ? '/pedidos-venda' : '/orcamentos';
  const [clienteId, setClienteId] = useState('');
  const [clienteNome, setClienteNome] = useState('');
  const [clienteInfo, setClienteInfo] = useState(null);
  const [documento, setDocumento] = useState('');
  const [observacao, setObservacao] = useState('');
  const [validade, setValidade] = useState('');
  const [desconto, setDesconto] = useState(0);
  const [itens, setItens] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clientesOpts, setClientesOpts] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [codigo, setCodigo] = useState('');
  const [status, setStatus] = useState('Aberto');
  const [formaPagamentoId, setFormaPagamentoId] = useState('');
  const [formasPagamento, setFormasPagamento] = useState([]);

  useEffect(() => {
    if (tipoFixo) setTipo(tipoFixo);
  }, [tipoFixo]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAllPaymentMethods();
        const lista = Array.isArray(data) ? data : data?.items || [];
        setFormasPagamento(lista.filter((f) => f.ativo !== false));
      } catch {
        setFormasPagamento([]);
      }
    })();
  }, []);

  const totalItens = useMemo(
    () => itens.reduce((s, i) => s + Number(i.valorTotal || 0), 0),
    [itens],
  );
  const total = Math.max(0, totalItens - Number(desconto || 0));

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await getPedidoComercial(id);
        setTipo(data.tipo || tipoFixo || 'Orcamento');
        setCodigo(data.codigo || '');
        setStatus(data.status || '');
        setClienteId(data.clienteId || '');
        setClienteNome(data.clienteNome || '');
        setClienteInfo(
          data.clienteId
            ? {
                documento: data.clienteDocumento || data.documento || '',
                telefone: data.clienteTelefone || '',
                email: data.clienteEmail || '',
                endereco: data.clienteEndereco || '',
                cidade: data.clienteCidade || '',
              }
            : null,
        );
        setDocumento(data.documento || data.clienteDocumento || '');
        setObservacao(data.observacao || '');
        setDesconto(Number(data.desconto || 0));
        setValidade(data.validade ? String(data.validade).slice(0, 10) : '');
        setFormaPagamentoId(data.formaPagamentoId ? String(data.formaPagamentoId) : '');
        setItens(
          (data.itens || []).map((i) => ({
            produtoId: i.produtoId,
            produtoCodigo: i.produtoCodigo,
            produtoDescricao: i.produtoDescricao,
            quantidade: Number(i.quantidade),
            valorUnitario: Number(i.valorUnitario),
            desconto: Number(i.desconto || 0),
            acrescimo: Number(i.acrescimo || 0),
            valorTotal: Number(i.valorTotal),
          })),
        );
      } catch (e) {
        setError(getApiErrorMessage(e, 'Erro ao carregar documento.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [editing, id]);

  const buscarClientes = useCallback(async (termo) => {
    if (!termo || termo.trim().length < 2) {
      setClientesOpts([]);
      return;
    }
    try {
      const { data } = await getAllClients({
        indice: 1,
        qtde: 15,
        termo: termo.trim(),
        estabelecimentoId: estabelecimentoId || undefined,
      });
      setClientesOpts(data?.items || data || []);
    } catch {
      setClientesOpts([]);
    }
  }, [estabelecimentoId]);

  const addProduto = async () => {
    setError('');
    const resultado = await processarBuscaProdutoEnter({
      termo: buscaProduto,
      estabelecimentoId: estabelecimentoId || undefined,
      ativo: true,
    });
    if (resultado.tipo === 'vazio') return;
    if (resultado.tipo === 'nao_encontrado') {
      setError(resultado.mensagem || 'Produto não encontrado.');
      return;
    }
    if (resultado.tipo === 'lista') {
      setError('Vários produtos encontrados — refine o código/descrição.');
      return;
    }
    const p = resultado.produto;
    const qtd = Number(resultado.quantidade || 1);
    const unit = precoVarejo(p);
    setItens((prev) => [
      ...prev,
      {
        produtoId: p.id,
        produtoCodigo: p.codigo,
        produtoDescricao: p.descricao,
        quantidade: qtd,
        valorUnitario: unit,
        desconto: 0,
        acrescimo: 0,
        valorTotal: Number((qtd * unit).toFixed(2)),
      },
    ]);
    setBuscaProduto('');
  };

  const updateItem = (idx, patch) => {
    setItens((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const next = { ...item, ...patch };
        if (!canAlterarPreco && patch.valorUnitario != null) {
          next.valorUnitario = item.valorUnitario;
        }
        const q = Number(next.quantidade) || 0;
        const u = Number(next.valorUnitario) || 0;
        const d = Number(next.desconto) || 0;
        next.valorTotal = Number((q * u - d).toFixed(2));
        return next;
      }),
    );
  };

  const handleSave = async () => {
    if (!estabelecimentoId) {
      setError('Selecione o estabelecimento.');
      return;
    }
    if (itens.length === 0) {
      setError('Inclua ao menos um item.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      estabelecimentoId: Number(estabelecimentoId),
      tipo,
      clienteId: clienteId ? Number(clienteId) : null,
      documento: documento || null,
      observacao: observacao || null,
      validade: validade || null,
      desconto: Number(desconto) || 0,
      acrescimo: 0,
      formaPagamentoId: formaPagamentoId ? Number(formaPagamentoId) : null,
      itens: itens.map((i) => ({
        produtoId: i.produtoId,
        quantidade: Number(i.quantidade),
        valorUnitario: Number(i.valorUnitario),
        desconto: Number(i.desconto) || 0,
        acrescimo: 0,
      })),
    };
    try {
      if (editing) {
        await updatePedidoComercial(id, payload);
      } else {
        const { data } = await createPedidoComercial(payload);
        navigate(`${basePath}/${data.id}`);
        return;
      }
      navigate(basePath);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  const rotuloCliente = (c) => {
    const nome = c.nomeFantasia || c.razaoSocial || `Cliente ${c.id}`;
    const doc = c.cnpj || c.cpf || '';
    const cidade = c.cidade
      ? `${c.cidade}${c.estado ? `/${c.estado}` : ''}`
      : '';
    const tel = c.telefone || '';
    return [nome, doc, tel, cidade].filter(Boolean).join(' · ');
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const readOnly = editing && status !== 'Aberto';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <PageHeader
        title={
          editing
            ? `${codigo || 'Documento'} · ${status}`
            : (tipo === 'Pedido' ? 'Novo pedido de venda' : 'Novo orçamento')
        }
        subtitle={
          tipo === 'Pedido'
            ? 'Pedido de venda (PED######), bipável no PDV.'
            : 'Orçamento (ORC######), bipável no PDV.'
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {!tipoFixo && (
            <TextField
              select
              label="Tipo"
              size="small"
              value={tipo}
              disabled={editing || readOnly}
              onChange={(e) => setTipo(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="Orcamento">Orçamento</MenuItem>
              <MenuItem value="Pedido">Pedido de venda</MenuItem>
            </TextField>
          )}
          <TextField
            label="Validade"
            type="date"
            size="small"
            value={validade}
            disabled={readOnly}
            onChange={(e) => setValidade(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Desconto geral (R$)"
            size="small"
            type="number"
            value={desconto}
            disabled={readOnly}
            onChange={(e) => setDesconto(e.target.value)}
            sx={{ width: 160 }}
          />
          <TextField
            select
            label="Forma de pagamento"
            size="small"
            value={formaPagamentoId}
            disabled={readOnly}
            onChange={(e) => setFormaPagamentoId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Não informado</MenuItem>
            {formasPagamento.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>
                {f.descricao}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ alignSelf: 'center', fontWeight: 800 }}>
            Total {money(total)}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography fontWeight={700} sx={{ mb: 1 }}>Cliente</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            label="Buscar cliente"
            size="small"
            value={buscaCliente}
            disabled={readOnly}
            onChange={(e) => {
              setBuscaCliente(e.target.value);
              buscarClientes(e.target.value);
            }}
            sx={{ minWidth: 260 }}
          />
          <TextField
            select
            label="Selecionado"
            size="small"
            value={clienteId || ''}
            disabled={readOnly}
            onChange={(e) => {
              const cid = e.target.value;
              setClienteId(cid);
              if (!cid) {
                setClienteNome('');
                setClienteInfo(null);
                setDocumento('');
                return;
              }
              const c = clientesOpts.find((x) => String(x.id) === String(cid));
              setClienteNome(c?.nomeFantasia || c?.razaoSocial || '');
              const doc = c?.cnpj || c?.cpf || c?.cnpJ || '';
              setDocumento(doc);
              setClienteInfo({
                documento: doc,
                telefone: c?.telefone || '',
                email: c?.email || '',
                endereco: [c?.logradouro, c?.numero, c?.bairro].filter(Boolean).join(', '),
                cidade: c?.cidade
                  ? `${c.cidade}${c.estado ? `/${c.estado}` : ''}`
                  : '',
              });
            }}
            sx={{ minWidth: 320, flex: 1 }}
          >
            <MenuItem value="">Consumidor</MenuItem>
            {clienteId && !clientesOpts.some((c) => String(c.id) === String(clienteId)) && (
              <MenuItem value={clienteId}>{clienteNome || `Cliente #${clienteId}`}</MenuItem>
            )}
            {clientesOpts.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {rotuloCliente(c)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="CPF/CNPJ"
            size="small"
            value={documento}
            disabled={readOnly}
            onChange={(e) => setDocumento(e.target.value)}
          />
        </Stack>
        {(clienteId || clienteInfo) && (
          <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover' }}>
            <Typography fontWeight={800}>
              {clienteNome || 'Cliente selecionado'}
            </Typography>
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              {(clienteInfo?.documento || documento) && (
                <Typography variant="body2">Documento: {clienteInfo?.documento || documento}</Typography>
              )}
              {clienteInfo?.telefone && (
                <Typography variant="body2">Telefone: {clienteInfo.telefone}</Typography>
              )}
              {clienteInfo?.email && (
                <Typography variant="body2">E-mail: {clienteInfo.email}</Typography>
              )}
              {clienteInfo?.endereco && (
                <Typography variant="body2">Endereço: {clienteInfo.endereco}</Typography>
              )}
              {clienteInfo?.cidade && (
                <Typography variant="body2">Cidade: {clienteInfo.cidade}</Typography>
              )}
              {!clienteInfo?.documento && !documento && !clienteInfo?.telefone && (
                <Typography variant="body2" color="text.secondary">
                  Sem dados adicionais no cadastro.
                </Typography>
              )}
            </Stack>
          </Paper>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1 }}>
          <TextField
            label="Produto"
            size="small"
            fullWidth
            value={buscaProduto}
            disabled={readOnly}
            helperText={PRODUCT_SEARCH_HINT}
            onChange={(e) => setBuscaProduto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addProduto();
              }
            }}
          />
          <Button variant="contained" disabled={readOnly} onClick={addProduto}>
            Incluir
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Qtd</TableCell>
              <TableCell align="right">Unit.</TableCell>
              <TableCell align="right">Desc. R$</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {itens.map((item, idx) => (
              <TableRow key={`${item.produtoId}-${idx}`}>
                <TableCell>{item.produtoCodigo}</TableCell>
                <TableCell>{item.produtoDescricao}</TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={item.quantidade}
                    disabled={readOnly}
                    onChange={(e) => updateItem(idx, { quantidade: e.target.value })}
                    sx={{ width: 90 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={item.valorUnitario}
                    disabled={readOnly || !canAlterarPreco}
                    onChange={(e) => updateItem(idx, { valorUnitario: e.target.value })}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={item.desconto}
                    disabled={readOnly}
                    onChange={(e) => updateItem(idx, { desconto: e.target.value })}
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell align="right">{money(item.valorTotal)}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    disabled={readOnly}
                    onClick={() => setItens((p) => p.filter((_, i) => i !== idx))}
                  >
                    Remover
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {itens.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                    Nenhum item.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <TextField
        label="Observação"
        fullWidth
        multiline
        minRows={2}
        value={observacao}
        disabled={readOnly}
        onChange={(e) => setObservacao(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>
          Voltar
        </Button>
        {!readOnly && (
          <Button variant="contained" disabled={saving} onClick={handleSave}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
